import React, { useEffect, useState } from 'react'
import { Router, CheckCircle2, XCircle, KeyRound, Plus, Trash2, Eye, EyeOff } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Table, THead, td, Field, Button, Alert, Banner, Modal, ModalHeader, Input } from '../components/ui'

export default function ModelOnt() {
  const [data, setData] = useState([])
  const [genieacsError, setGenieacsError] = useState(null)
  const [loading, setLoading] = useState(true)

  const [credModal, setCredModal] = useState(null) // nama model yg lagi dibuka
  const [creds, setCreds] = useState([])
  const [credLoading, setCredLoading] = useState(false)
  const [form, setForm] = useState({ username: '', password: '', catatan: '' })
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [showPw, setShowPw] = useState({})
  const [revealed, setRevealed] = useState({})

  useEffect(() => { load() }, [])

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/ont-models')
      setData(res.data.rows)
      setGenieacsError(res.data.genieacsError)
    } finally { setLoading(false) }
  }

  async function bukaKredensial(model) {
    setCredModal(model)
    setForm({ username: '', password: '', catatan: '' })
    setError('')
    setShowPw({})
    setRevealed({})
    setCredLoading(true)
    try {
      const res = await api.get('/ont-credentials', { params: { model } })
      setCreds(res.data)
    } finally { setCredLoading(false) }
  }

  async function simpanKredensial() {
    setError('')
    if (!form.username.trim()) return setError('Username wajib diisi')
    if (!form.password) return setError('Password wajib diisi')
    setSaving(true)
    try {
      await api.post('/ont-credentials', { model: credModal, ...form })
      const res = await api.get('/ont-credentials', { params: { model: credModal } })
      setCreds(res.data)
      setForm({ username: '', password: '', catatan: '' })
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    } finally { setSaving(false) }
  }

  async function toggleShowPw(id) {
    if (!showPw[id] && revealed[id] === undefined) {
      const res = await api.get(`/ont-credentials/${id}/reveal`)
      setRevealed(r => ({ ...r, [id]: res.data.password }))
    }
    setShowPw(s => ({ ...s, [id]: !s[id] }))
  }

  async function hapusKredensial(id) {
    if (!window.confirm('Hapus kredensial ini dari bank?')) return
    await api.delete(`/ont-credentials/${id}`)
    setCreds(creds.filter(c => c.id !== id))
    load()
  }

  return (
    <div>
      <PageHeader
        title="Model ONT"
        subtitle="Status dukungan Sync GenieACS & Ganti Password WiFi per model, plus bank kredensial admin yang dicoba bot saat login ke ONT/router"
      />

      {genieacsError && (
        <Banner tone="danger">{genieacsError} — jumlah pelanggan di bawah cuma mencakup yang belum terdaftar GenieACS, belum termasuk yang sudah terdaftar.</Banner>
      )}

      <Table>
        <THead cols={['Model', 'Jumlah Pelanggan', 'Sync GenieACS', 'Ganti Password WiFi', 'Kredensial']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={5}><EmptyState icon={Router} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={5}><EmptyState icon={Router} title="Belum ada model ONT yang terdeteksi" hint="Jalankan 'Cek Jenis Modem' di halaman Perangkat Pelanggan" /></td></tr>
          ) : data.map(m => (
            <tr key={m.model}>
              <td style={{ ...td, fontWeight: 600 }}>{m.model}</td>
              <td style={td}><Badge tone="neutral">{m.jumlahPelanggan}</Badge></td>
              <td style={td}>
                {m.syncSupported
                  ? <Badge tone="success"><CheckCircle2 size={12} /> Didukung</Badge>
                  : <Badge tone="danger"><XCircle size={12} /> Belum</Badge>}
              </td>
              <td style={td}>
                {m.wifiSupported
                  ? <Badge tone="success"><CheckCircle2 size={12} /> Didukung</Badge>
                  : <Badge tone="danger"><XCircle size={12} /> Belum</Badge>}
              </td>
              <td style={td}>
                <Button size="sm" variant="soft" icon={KeyRound} onClick={() => bukaKredensial(m.model)}>
                  {m.jumlahKredensial} kredensial
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {credModal && (
        <Modal onClose={() => setCredModal(null)} width={520}>
          <ModalHeader
            title={`Kredensial — ${credModal}`}
            subtitle="Bot akan mencoba tiap kredensial di bawah satu per satu saat login ke ONT model ini, dimulai dari yang paling sering berhasil"
            onClose={() => setCredModal(null)}
          />

          {error && <Alert type="error">{error}</Alert>}

          <div style={{ marginBottom: 18 }}>
            {credLoading ? (
              <div style={{ padding: '14px 0', color: 'var(--text-muted)', fontSize: 13 }}>Memuat...</div>
            ) : creds.length === 0 ? (
              <div style={{ padding: '14px 0', color: 'var(--text-muted)', fontSize: 13 }}>Belum ada kredensial tersimpan di bank untuk model ini — bot masih pakai default bawaan (env var).</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {creds.map(c => (
                  <div key={c.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                    border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', fontSize: 13
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{c.username}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-muted)', fontSize: 12.5, marginTop: 2 }}>
                        <span style={{ fontFamily: 'monospace' }}>{showPw[c.id] ? revealed[c.id] : '••••••••'}</span>
                        <button onClick={() => toggleShowPw(c.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
                          {showPw[c.id] ? <EyeOff size={13} /> : <Eye size={13} />}
                        </button>
                      </div>
                      {c.catatan && <div style={{ fontSize: 11.5, color: 'var(--text-faint)', marginTop: 2 }}>{c.catatan}</div>}
                    </div>
                    <Badge tone={c.success_count > 0 ? 'success' : 'neutral'}>
                      {c.success_count > 0 ? `${c.success_count}x berhasil` : 'belum pernah dicoba'}
                    </Badge>
                    <button onClick={() => hapusKredensial(c.id)} title="Hapus" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)' }}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.3 }}>
              Tambah Kredensial Baru
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <Field label="Username" style={{ flex: 1 }}>
                <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="mis. admin" />
              </Field>
              <Field label="Password" style={{ flex: 1 }}>
                <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="mis. admin123" />
              </Field>
            </div>
            <Field label="Catatan (opsional)" style={{ marginBottom: 14 }}>
              <Input value={form.catatan} onChange={e => setForm({ ...form, catatan: e.target.value })} placeholder="mis. ditemukan di pelanggan budi-kdw" />
            </Field>
            <Button icon={Plus} loading={saving} onClick={simpanKredensial} style={{ width: '100%', justifyContent: 'center' }}>
              Tambah ke Bank
            </Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
